import React from 'react';
import PortfolioGanttView from './PortfolioGanttView';

const STATUS_COLOR = {
  Draft: '#9e9e9e',
  Active: '#4caf50',
  OnHold: '#ff9800',
  Completed: '#2196f3',
  Cancelled: '#f44336',
};

function formatStatus(s) { return s === 'OnHold' ? 'On Hold' : s; }
const EM_DASH = '—';
function formatDate(iso) {
  if (!iso) return EM_DASH;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return EM_DASH;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

const FILTERS = [
  { key: 'All', label: 'All' },
  { key: 'Draft', label: 'Draft' },
  { key: 'Active', label: 'Active' },
  { key: 'OnHold', label: 'On Hold' },
  { key: 'Completed', label: 'Completed' },
  { key: 'Cancelled', label: 'Cancelled' },
];

function renderToolbar(p) {
  return (
    <div className="pc-tbar">
      <div className="view-toggle">
        <button
          className={p.view === 'normal' ? 'active' : ''}
          onClick={() => p.handleViewChange('normal')}
          type="button"
        >Normal</button>
        <button
          className={p.view === 'gantt' ? 'active' : ''}
          onClick={() => p.handleViewChange('gantt')}
          type="button"
        >Gantt</button>
      </div>
      {!p.isMyMode && (
        <>
          <div className="pc-sep" />
          <button className="pc-tb pri" onClick={p.onCreateClick} data-testid="pl-new-project-btn">+ New Project</button>
          <button className="pc-tb" onClick={() => p.navigate('/projects/import')}>
            <span role="img" aria-label="import">&#x1F4E5;</span> Import
          </button>
        </>
      )}
      <div className="pc-sep" />
      {FILTERS.map(f => (
        <button
          key={f.key}
          className={'pc-tb' + (p.activeFilter === f.key ? ' on' : '')}
          onClick={() => p.setActiveFilter(f.key)}
        >{f.label}</button>
      ))}
      <div className="pc-sep" />
      <button className="pc-tb" onClick={p.load} title="Refresh">&#x21BB;</button>
      <div className="pc-search">
        <input
          placeholder="Search..."
          value={p.search}
          onChange={(e) => p.setSearch(e.target.value)}
        />
      </div>
    </div>
  );
}

function renderHead(p) {
  // PLX-S1: width comes from the persisted colWidths; each header carries a
  // drag-resize handle. stopPropagation keeps the resize off the sort toggle.
  const th = (col, defaultStyle, label, cls) => {
    const width = p.colWidths[col] ?? defaultStyle.width ?? defaultStyle.minWidth;
    const style = { ...defaultStyle, width };
    return (
      <th style={style} className={cls} onClick={() => p.toggleSort(col)}>
        {label}<span className="pc-s">{p.sortArrow(col)}</span>
        <span
          className="col-resizer"
          onMouseDown={(e) => { e.stopPropagation(); p.startColResize(e, col); }}
          data-testid={`pl-col-resizer-${col}`}
        />
      </th>
    );
  };
  return (
    <thead>
      <tr>
        <th style={{ width: 24, padding: '6px 4px' }} />
        {th('name', { minWidth: 220 }, 'Project Name')}
        {/* MYPROJ-S1 — in "my" mode the four signal columns land IMMEDIATELY
            after Project Name (before Code / Client / dates) so the consultant
            reads their own workload first. PM mode keeps the empty 24px
            actions cell + no signal columns. */}
        {p.isMyMode ? (
          <>
            {th('myOpen', { width: 80 }, 'My open', 'pc-r')}
            {th('forTakeover', { width: 100 }, 'For takeover', 'pc-r')}
            {th('late', { width: 60 }, 'Late', 'pc-r')}
            {th('myHours', { width: 90 }, 'My hours', 'pc-r')}
          </>
        ) : (
          <th style={{ width: 24 }} />
        )}
        {th('code', { width: 90 }, 'Code')}
        {th('client', { width: 130 }, 'Client')}
        {th('start', { width: 90 }, 'Start')}
        {th('end', { width: 90 }, 'End')}
        {th('durationDays', { width: 80 }, 'Duration', 'pc-r')}
        {th('completionPercent', { width: 120 }, 'Completion')}
        {th('status', { width: 90 }, 'Status')}
      </tr>
    </thead>
  );
}

function renderRows(p) {
  if (p.loading) {
    return <tbody><tr><td colSpan={10} className="pc-empty">Loading...</td></tr></tbody>;
  }
  if (p.error) {
    return <tbody><tr><td colSpan={10} className="pc-empty" style={{ color: '#c62828' }}>{p.error}</td></tr></tbody>;
  }
  if (!p.sorted.length) {
    if (!p.rows.length) {
      return (
        <tbody><tr><td colSpan={10} className="pc-empty">
          <div className="pc-empty-box">
            <div className="pc-empty-title">No projects yet</div>
            <button className="pc-tb pri" onClick={p.onCreateClick}>+ Add your first project</button>
          </div>
        </td></tr></tbody>
      );
    }
    return <tbody><tr><td colSpan={10} className="pc-empty">No projects found.</td></tr></tbody>;
  }
  return (
    <tbody>
      {p.sorted.map(r => {
        const ic = STATUS_COLOR[r.status] || '#9e9e9e';
        const il = (r.status || '?').charAt(0);
        const hasDates = !!(r.startDate && r.endDate);
        const pct = Math.max(0, Math.min(100, Number(r.completionPercent) || 0));
        return (
          <tr
            key={r.id}
            className={p.selectedId === r.id ? 'pc-sel' : ''}
            onClick={() => p.handleRowClick(r.id)}
            onDoubleClick={() => p.handleRowDouble(r.id)}
          >
            <td style={{ textAlign: 'center', padding: '5px 4px' }}>
              <span className="pc-pi" style={{ background: ic }}>{il}</span>
            </td>
            <td className="pc-name-cell">
              <a
                href={`${p.linkBase || '/projects'}/${r.id}`}
                className="pc-name-link"
                onClick={(e) => { e.preventDefault(); p.handleRowDouble(r.id); }}
              >{r.name}</a>
            </td>
            {p.isMyMode ? (() => {
              const s = (p.signals || {})[r.id] || {};
              const dim = !(s.myOpen || s.forTakeover || s.late || s.myHours);
              const opacity = dim ? 0.45 : 1;
              return (
                <>
                  <td className="pc-r" style={{ opacity }} data-testid={`pl-my-open-${r.id}`}>{s.myOpen || 0}</td>
                  <td className="pc-r" style={{ opacity }} data-testid={`pl-my-takeover-${r.id}`}>{s.forTakeover || 0}</td>
                  <td className="pc-r"
                      style={{ opacity, color: (s.late || 0) > 0 ? '#c0392b' : undefined, fontWeight: (s.late || 0) > 0 ? 700 : undefined }}
                      data-testid={`pl-my-late-${r.id}`}>
                    {s.late || 0}
                  </td>
                  <td className="pc-r" style={{ opacity }} data-testid={`pl-my-hours-${r.id}`}>
                    {Number(s.myHours || 0).toFixed(1)}
                  </td>
                </>
              );
            })() : (
              <td style={{ textAlign: 'center' }}>
                <span
                  className="pc-dots"
                  onClick={(e) => p.openCtx(e, r.id)}
                >&#xB7;&#xB7;&#xB7;</span>
              </td>
            )}
            <td>{r.code}</td>
            <td style={{ color: '#666' }}>{r.client || EM_DASH}</td>
            <td className="pc-date">{formatDate(r.startDate)}</td>
            <td className="pc-date">{formatDate(r.endDate)}</td>
            <td className="pc-r">{hasDates ? `${r.durationDays}d` : EM_DASH}</td>
            <td>
              <div className="completion-cell">
                <span className="completion-text">{pct}%</span>
                <div className="completion-bar">
                  <div className="completion-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </td>
            <td><span className={'pc-st pc-st-' + r.status}>{formatStatus(r.status)}</span></td>
          </tr>
        );
      })}
    </tbody>
  );
}

function renderCtxMenu(p) {
  if (!p.ctx.open) return null;
  const stop = (e) => e.stopPropagation();
  const pick = (action) => {
    p.setCtx(c => ({ ...c, open: false }));
    const id = p.ctx.id;
    if (action === 'open') p.navigate(`/projects/${id}`);
    else if (action === 'active') p.showSnack('Status change — coming in P2');
    else if (action === 'hold') p.showSnack('Status change — coming in P2');
    else if (action === 'complete') p.showSnack('Status change — coming in P2');
    else if (action === 'delete') p.askDelete(id);
  };
  return (
    <div
      className="pc-ctx"
      style={{ left: p.ctx.x, top: p.ctx.y }}
      onClick={stop}
    >
      <div className="pc-ctx-i" onClick={() => pick('open')}>Open Project</div>
      <div className="pc-ctx-i pc-dis">Download Plan (JSON)</div>
      <div className="pc-ctx-s" />
      <div className="pc-ctx-i" onClick={() => pick('active')}>Set Active</div>
      <div className="pc-ctx-i" onClick={() => pick('hold')}>Set On Hold</div>
      <div className="pc-ctx-i" onClick={() => pick('complete')}>Set Completed</div>
      <div className="pc-ctx-s" />
      <div className="pc-ctx-i pc-red" onClick={() => pick('delete')}>Delete Project</div>
    </div>
  );
}
function renderDeleteModal(p) {
  if (!p.delTarget) return null;
  const t = p.delTarget;
  return (
    <div className="pc-mo" onClick={p.closeDel}>
      <div className="pc-md" onClick={(e) => e.stopPropagation()}>
        <div className="pc-md-h">Delete Project</div>
        <div className="pc-md-b">
          <div className="pc-del-msg">
            Are you sure you want to delete <strong>{t.name}</strong> ({t.code})?
            <br /><br />
            This action cannot be undone. All tasks, roles, and allocations will be permanently removed.
          </div>
        </div>
        <div className="pc-md-f">
          <button className="pc-tb" onClick={p.closeDel} disabled={p.deleting}>Cancel</button>
          <button className="pc-tb pc-btn-danger" onClick={p.doDelete} disabled={p.deleting}>
            {p.deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function renderPage(p) {
  const count = p.sorted.length;
  const totalLoaded = p.rows.length;
  const suffix = count !== 1 ? 's' : '';
  const info = count + ' project' + suffix + (count !== totalLoaded ? ' (of ' + totalLoaded + ')' : '');
  return (
    <div className="pc-root">
      <div className="pc-hdr"><h1>Project Center</h1></div>
      {renderToolbar(p)}
      {p.view === 'gantt' ? (
        <PortfolioGanttView projects={p.rows} navigate={p.navigate} onCreate={p.onCreateClick} />
      ) : (
        <div className="pc-grid-wrap">
          <table className="pc-table">
            {renderHead(p)}
            {renderRows(p)}
          </table>
        </div>
      )}
      <div className="pc-sbar">
        <span>{info}</span>
        <span>HRApp &mdash; Project Center</span>
      </div>
      {renderCtxMenu(p)}
      {renderDeleteModal(p)}
      <div className={'pc-snack' + (p.snack ? ' pc-show' : '')}>{p.snack}</div>
    </div>
  );
}

